/**
 * Reminder Types - Feature A5
 * Multi-reminder system with PWA notifications
 */

/**
 * Single reminder configuration
 */
export interface Reminder {
    id: string;
    daysBefore: number;
    time: string; // HH:mm format, e.g., "09:00"
    enabled: boolean;
    notified?: boolean; // Track if already sent
}

/**
 * Preset reminder options
 */
export const REMINDER_PRESETS = [
    { daysBefore: 7, label: '7 ngày trước' },
    { daysBefore: 3, label: '3 ngày trước' },
    { daysBefore: 1, label: '1 ngày trước' },
] as const;

/**
 * Default reminders by event type
 * Uses defaultReminders from EVENT_TYPE_CONFIGS
 */
export const DEFAULT_REMINDER_TIME = '09:00';

/**
 * Generate unique reminder ID
 */
export const generateReminderId = (): string => {
    return `reminder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a reminder from days before
 */
export const createReminder = (daysBefore: number, enabled = true): Reminder => ({
    id: generateReminderId(),
    daysBefore,
    time: DEFAULT_REMINDER_TIME,
    enabled,
    notified: false,
});

/**
 * Create default reminders from event type config
 */
export const createDefaultReminders = (defaultDays: number[]): Reminder[] => {
    return defaultDays.map(days => createReminder(days, true));
};

/**
 * Calculate reminder date
 */
export const getReminderDate = (eventDate: Date, daysBefore: number): Date => {
    const reminderDate = new Date(eventDate);
    reminderDate.setDate(reminderDate.getDate() - daysBefore);
    return reminderDate;
};

/**
 * Check if reminder should fire today
 */
export const shouldFireToday = (
    eventDate: Date,
    reminder: Reminder,
    today: Date = new Date()
): boolean => {
    if (!reminder.enabled || reminder.notified) return false;

    const reminderDate = getReminderDate(eventDate, reminder.daysBefore);
    return (
        reminderDate.getFullYear() === today.getFullYear() &&
        reminderDate.getMonth() === today.getMonth() &&
        reminderDate.getDate() === today.getDate()
    );
};

/**
 * Format reminder for display
 */
export const formatReminderPreview = (eventDate: Date, daysBefore: number): string => {
    const reminderDate = getReminderDate(eventDate, daysBefore);
    return reminderDate.toLocaleDateString('vi-VN', {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
    });
};

/**
 * Get upcoming reminders count
 */
export const getUpcomingRemindersCount = (reminders: Reminder[]): number => {
    return reminders.filter(r => r.enabled && !r.notified).length;
};
